<template>
  <div>
    <v-btn outlined color="teal" @click="dialog = true">
      <v-icon left>mdi-file-import-outline</v-icon>Importa .md
    </v-btn>

    <v-dialog v-model="dialog" width="800" scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium">
          Importa schede da file .md
          <v-spacer />
          <v-btn icon small @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text style="height: 580px">

          <!-- Upload -->
          <template v-if="!parsed">
            <p class="text-body-2 grey--text mt-4 mb-4">
              Carica un file <code>.md</code> contenente una o più schede pianta nel formato
              standard. Le informazioni parsate verranno precaricate nel form di modifica
              senza salvare nulla — il salvataggio resta un'azione esplicita dell'utente.
            </p>
            <v-file-input
              v-model="file"
              accept=".md,.txt"
              label="Seleziona file .md"
              outlined
              prepend-icon="mdi-file-document-outline"
              hide-details
              :loading="loading"
            />
          </template>

          <!-- Results -->
          <template v-else>
            <v-alert v-if="parsed.global_error" type="error" class="mt-3">
              {{ parsed.global_error }}
            </v-alert>

            <template v-else>
              <div class="d-flex align-center mt-3 mb-3">
                <span class="text-body-2 grey--text">
                  {{ parsed.results.length }} specie nel file
                </span>
                <v-spacer />
                <v-btn text x-small color="teal" @click="reset()">
                  <v-icon left x-small>mdi-refresh</v-icon>Carica altro file
                </v-btn>
              </div>

              <v-expansion-panels accordion flat>
                <v-expansion-panel v-for="(r, i) in parsed.results" :key="i" class="mb-1">
                  <v-expansion-panel-header style="border: 1px solid #e0e0e0; border-radius: 4px">
                    <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap">
                      <v-chip x-small dark :color="status_color(r)">
                        {{ status_label(r) }}
                      </v-chip>
                      <span class="font-weight-medium text-body-2">{{ r.nome_specie }}</span>
                      <v-chip x-small outlined color="red" v-if="r.errors.length">
                        {{ r.errors.length }} errori
                      </v-chip>
                      <v-chip x-small outlined color="orange" v-if="r.warnings.length">
                        {{ r.warnings.length }} avvisi
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>

                    <!-- Errors (bloccanti) -->
                    <v-alert
                      v-for="(e, ei) in r.errors"
                      :key="'e' + ei"
                      type="error" dense text class="mt-2 mb-1"
                      style="font-size: 13px"
                    >{{ e }}</v-alert>

                    <!-- Warnings -->
                    <v-alert
                      v-for="(w, wi) in r.warnings"
                      :key="'w' + wi"
                      type="warning" dense text class="mt-2 mb-1"
                      style="font-size: 13px"
                    >{{ w }}</v-alert>

                    <!-- Ambiguous: radio selector -->
                    <template v-if="r.match_status === 'ambiguous'">
                      <div class="text-caption grey--text mt-2 mb-1">
                        Più specie corrispondono al nome — selezionane una:
                      </div>
                      <v-radio-group v-model="r.record_id" dense hide-details class="mt-0 mb-2">
                        <v-radio
                          v-for="c in r.candidates"
                          :key="c.id"
                          :label="c.nome"
                          :value="c.id"
                        />
                      </v-radio-group>
                    </template>

                    <!-- Not found: suggestions -->
                    <template v-if="r.match_status === 'not_found'">
                      <v-alert type="error" dense text class="mt-2" style="font-size: 13px">
                        Specie "{{ r.nome_specie }}" non trovata nel gestionale.
                      </v-alert>
                      <template v-if="r.candidates.length">
                        <div class="text-caption grey--text mt-2 mb-1">Possibili corrispondenze:</div>
                        <v-chip-group column>
                          <v-chip
                            v-for="c in r.candidates"
                            :key="c.id"
                            small outlined color="teal"
                            @click="r.match_status = 'found'; r.record_id = c.id"
                          >{{ c.nome }}</v-chip>
                        </v-chip-group>
                      </template>
                    </template>

                    <!-- Fields preview -->
                    <v-simple-table dense class="mt-3" v-if="Object.keys(r.campi).length">
                      <thead>
                        <tr>
                          <th style="width: 160px" class="text-caption">Campo</th>
                          <th class="text-caption">Anteprima contenuto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(val, key) in r.campi" :key="key">
                          <td class="text-caption grey--text">{{ campo_label(key) }}</td>
                          <td class="text-caption" style="white-space: pre-wrap">
                            {{ val.length > 130 ? val.substring(0, 130) + '…' : val }}
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>

                    <div class="d-flex justify-end mt-3">
                      <v-btn
                        small color="teal" dark
                        :disabled="!can_precarica(r)"
                        @click="precarica(r)"
                      >
                        <v-icon left small>mdi-clipboard-arrow-down-outline</v-icon>
                        Precarica scheda
                      </v-btn>
                    </div>

                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>
          </template>

        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="dialog = false">Chiudi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <InformazioniSpecie
      v-if="preload_id_specie"
      :key="preload_id_specie"
      ref="info_dialog"
      :id_specie="preload_id_specie"
    />
  </div>
</template>

<script>
import InformazioniSpecie from "./informazioni_specie.vue";
import { parseMd } from "./md_parser.js";

const CAMPO_LABELS = {
  info_generali:    "Info Generali",
  info_rinvaso:     "Rinvaso",
  info_esposizione: "Esposizione",
  info_irrigazione: "Irrigazione",
  info_manutenzione:"Manutenzione",
  info_malattie:    "Malattie",
  info_curiosita:   "Curiosità",
  info_esperti:     "Per i più esperti",
};

export default {
  components: { InformazioniSpecie },
  data() {
    return {
      dialog: false,
      file: null,
      parsed: null,
      loading: false,
      preload_id_specie: null,
    };
  },
  computed: {
    specie_list() {
      return this.$store.getters.get_specie;
    },
  },
  watch: {
    file(val) {
      if (val) this.esegui_parsing();
    },
  },
  methods: {
    reset() {
      this.file = null;
      this.parsed = null;
    },
    async esegui_parsing() {
      this.loading = true;
      try {
        const text = await this.file.text();
        this.parsed = parseMd(text, this.specie_list);
      } finally {
        this.loading = false;
      }
    },
    can_precarica(r) {
      return (
        r.errors.length === 0 &&
        (r.match_status === "found" ||
          (r.match_status === "ambiguous" && r.record_id))
      );
    },
    status_color(r) {
      if (r.errors.length) return "red";
      if (r.match_status === "found") return "green";
      if (r.match_status === "ambiguous") return "orange";
      return "red darken-2";
    },
    status_label(r) {
      if (r.errors.length) return "errori";
      if (r.match_status === "found") return "trovata";
      if (r.match_status === "ambiguous") return "ambigua";
      return "non trovata";
    },
    campo_label(key) {
      return CAMPO_LABELS[key] || key;
    },
    precarica(r) {
      this.preload_id_specie = r.record_id;
      this.$nextTick(() => {
        this.$refs.info_dialog.openWithPreload(r.campi);
      });
    },
  },
};
</script>
